import React, { useMemo } from 'react';
import { AdvancedFilter, AdvancedFilterCondition, conditionTypeOptions } from '@round/ui-kit';
import { CategoriesFilterCondition } from './CategoriesFilter.helpers';
import { GenericDropdownOption } from '../../../../../../../utils/utils.types';
import styles from './CategoriesFilter.module.css';
import { ValueType } from 'react-select';
import { useContentTags } from '../../../../../../../contexts/ContentTagsContext';
import useAbortableEffect from '../../../../../../../hooks/useAbortableEffect';

type Props = {
    conditions: CategoriesFilterCondition[];
    onChange: (value: CategoriesFilterCondition[]) => void;
};

const CategoriesFilter = ({ conditions, onChange }: Props) => {
    const { tags, isLoading: isTagsLoading, isInitialized: isTagsInitialized, init: initTags } = useContentTags();

    useAbortableEffect(
        (signal) => {
            if (!isTagsInitialized) {
                initTags({ signal });
            }
        },
        [isTagsInitialized, initTags]
    );

    const options = useMemo(
        () =>
            tags.map((tag) => ({
                label: tag.name,
                value: tag.id,
            })),
        [tags]
    );

    const mappedConditions: AdvancedFilterCondition<ValueType<GenericDropdownOption<number>, true>>[] = useMemo(
        () =>
            conditions.map((condition) => {
                const tags = condition.value
                    .map((tagId) => options.find((tag) => tag.value === tagId))
                    .filter((tag): tag is GenericDropdownOption<number> => !!tag);

                return {
                    type: condition.type,
                    value: tags,
                };
            }),
        [options, conditions]
    );

    return (
        <AdvancedFilter
            className={styles.filter}
            conditions={mappedConditions}
            conditionOptions={conditionTypeOptions}
            onChange={(conditions) =>
                onChange(
                    conditions.map((condition) => ({
                        ...condition,
                        value: condition.value?.map((tag) => tag.value) ?? [],
                    }))
                )
            }
            options={options}
            optionsLoading={isTagsLoading}
            getLabel={(isFiltered) => (isFiltered ? 'Filtered by categories' : 'Categories')}
        />
    );
};

export default CategoriesFilter;
